<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useNotificationsStore } from "@/stores/notifications";
import type { Notification } from "@/api/models";

const notifications = useNotificationsStore();
const router = useRouter();

const open = ref(false);
const root = ref<HTMLElement | null>(null);

const recent = computed<Notification[]>(() =>
  notifications.unreadList.slice(0, 8),
);

const badge = computed(() => {
  if (notifications.unreadCount <= 0) return "";
  if (notifications.unreadCount > 99) return "99+";
  return String(notifications.unreadCount);
});

async function toggle(): Promise<void> {
  open.value = !open.value;
  if (open.value) {
    // Lazy-load the dropdown contents on first reveal so the topbar
    // doesn't pay for the list payload on every page nav.
    await notifications.fetchList("unread");
  }
}

function onClickOutside(e: MouseEvent): void {
  if (!root.value) return;
  if (!root.value.contains(e.target as Node)) {
    open.value = false;
  }
}

async function onPick(n: Notification): Promise<void> {
  open.value = false;
  if (!n.readAt) {
    try {
      await notifications.markRead(n.id);
    } catch {
      // Soft-fail; navigation is the main thing.
    }
  }
  if (n.linkUrl) {
    void router.push(n.linkUrl);
  }
}

function goAll(): void {
  open.value = false;
  void router.push("/notifications");
}

onMounted(() => {
  document.addEventListener("click", onClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onClickOutside);
});
</script>

<template>
  <div ref="root" class="bell-wrap">
    <button
      type="button"
      class="bell-btn"
      :class="{ active: open }"
      :aria-label="`Ilmoitukset (${notifications.unreadCount} lukematonta)`"
      @click="toggle"
    >
      <span class="bell-icon" aria-hidden="true">🔔</span>
      <span v-if="badge" class="badge">{{ badge }}</span>
    </button>
    <div v-if="open" class="dropdown" role="menu">
      <header class="dropdown-head">
        <strong>Ilmoitukset</strong>
        <span class="muted">{{ notifications.unreadCount }} lukematonta</span>
      </header>
      <div v-if="notifications.loading" class="dd-state">Ladataan…</div>
      <div v-else-if="recent.length === 0" class="dd-state">
        Ei lukemattomia ilmoituksia.
      </div>
      <ul v-else class="dd-list">
        <li
          v-for="n in recent"
          :key="n.id"
          class="dd-item"
          :class="`severity-${n.severity.toLowerCase()}`"
          @click="onPick(n)"
        >
          <div class="dd-title">{{ n.title }}</div>
          <div v-if="n.body" class="dd-body">{{ n.body }}</div>
        </li>
      </ul>
      <footer class="dropdown-foot">
        <button type="button" class="link-all" @click="goAll">
          Avaa kaikki →
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.bell-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.bell-btn {
  position: relative;
  background: transparent;
  border: 1px solid transparent;
  padding: 0.25rem 0.45rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.05rem;
  line-height: 1;
}
.bell-btn:hover,
.bell-btn.active {
  background: var(--surface-3);
  border-color: var(--border);
}
.bell-icon {
  display: inline-block;
}
.badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: #b91c1c;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 320px;
  max-width: 380px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgb(0 0 0 / 0.08);
  z-index: 50;
  overflow: hidden;
}
.dropdown-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
}
.muted {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.dd-state {
  padding: 0.85rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}
.dd-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 360px;
  overflow-y: auto;
}
.dd-item {
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  border-left: 3px solid #1e40af;
}
.dd-item:last-child {
  border-bottom: none;
}
.dd-item:hover {
  background: var(--surface-2);
}
.dd-item.severity-warning {
  border-left-color: #b45309;
}
.dd-item.severity-critical {
  border-left-color: #b91c1c;
}
.dd-title {
  font-weight: 600;
  color: var(--text-strong);
  font-size: 0.9rem;
}
.dd-body {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.dropdown-foot {
  padding: 0.5rem 0.85rem;
  background: var(--surface-2);
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
}
.link-all {
  background: transparent;
  border: none;
  color: var(--accent);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.15rem 0.3rem;
}
.link-all:hover {
  text-decoration: underline;
}
</style>
